import { useState } from 'react'
import { Info, Megaphone, Pencil, Pin, Plus } from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useAnnouncements, useEngagementDashboard, useEngagementMutations } from '../api'
import { ANNOUNCEMENT_STATUS_LABELS } from '../types'
import type { AnnouncementStatus } from '../types'

/** The notice board, and how far each notice actually got. */
export function AnnouncementsPage() {
  useBreadcrumb()
  const announcements = useAnnouncements()
  const dashboard = useEngagementDashboard()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const {
    saveAnnouncement, publishAnnouncement, archiveAnnouncement,
  } = useEngagementMutations()

  const [draft, setDraft] = useState<{
    id?: string; title: string; body: string; expiresOn: string; pinned: boolean
  } | null>(null)
  const [archiving, setArchiving] = useState<{ id: string; title: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Announcements"
        subtitle="What was said to everybody, and how many of them opened it."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ title: '', body: '', expiresOn: '', pinned: false })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New announcement
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {dashboard.isSuccess && (
        <Card className="space-y-1 p-4 text-sm text-slate-700">
          <p>
            {dashboard.data.participationPercent == null
              ? 'No survey has been sent yet.'
              : `Survey participation ${dashboard.data.participationPercent.toFixed(0)}% (${dashboard.data.responses} of ${dashboard.data.invitations}).`}
            {' '}
            {dashboard.data.announcementReachPercent == null
              ? 'No announcement has been published yet.'
              : `Announcement reach ${dashboard.data.announcementReachPercent.toFixed(0)}%.`}
          </p>
          <p className="flex items-start gap-2 text-slate-600">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {dashboard.data.note}
          </p>
        </Card>
      )}

      <Card>
        {announcements.isPending && <Skeleton className="m-4 h-24" />}
        {announcements.isError && (
          <ErrorState error={announcements.error} onRetry={() => void announcements.refetch()} />
        )}
        {announcements.isSuccess && announcements.data.length === 0 && (
          <EmptyState
            title="Nothing has been announced"
            description="A published announcement records the size of its audience at that moment, so its read figure does not move when somebody joins."
          />
        )}
        {announcements.isSuccess && announcements.data.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {announcements.data.map((row) => (
              <li key={row.id} className="space-y-2 p-4 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="flex items-center gap-2 font-medium text-[var(--color-heading)]">
                    {row.pinned
                      ? <Pin className="size-4 text-amber-600" aria-hidden="true" />
                      : <Megaphone className="size-4 text-slate-500" aria-hidden="true" />}
                    {row.title}
                  </h2>
                  <div className="flex items-center gap-2">
                    <StatusBadge
                      status={row.status}
                      tone={row.status === 'PUBLISHED' ? 'success' : 'neutral'}
                      label={ANNOUNCEMENT_STATUS_LABELS[row.status as AnnouncementStatus]
                        ?? row.status}
                    />
                    {row.status === 'DRAFT' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          void (async () => {
                            setError(null)
                            try {
                              await publishAnnouncement.mutateAsync(row.id)
                            } catch (caught) {
                              setError(toApiProblem(caught)?.detail
                                ?? 'That announcement could not be published')
                            }
                          })()
                        }}
                      >
                        Publish
                      </Button>
                    )}
                    <Button
                      type="button" variant="ghost"
                      aria-label={`Edit ${row.title}`}
                      onClick={() => {
                        setError(null)
                        setDraft({
                          id: row.id,
                          title: row.title,
                          body: row.body,
                          expiresOn: row.expiresOn ?? '',
                          pinned: row.pinned,
                        })
                      }}
                    >
                      <Pencil className="size-4" aria-hidden="true" />
                      Edit
                    </Button>
                    {row.status !== 'ARCHIVED' && (
                      <Button
                        type="button" variant="outline"
                        onClick={() => {
                          setError(null)
                          setArchiving({ id: row.id, title: row.title })
                        }}
                      >
                        Archive
                      </Button>
                    )}
                  </div>
                </div>
                <p className="whitespace-pre-wrap text-slate-700">{row.body}</p>
                <p className="text-xs text-slate-600">{row.reachNote}</p>
                {row.expiresOn && (
                  <p className="text-xs text-slate-500">
                    Comes down <DateValue value={row.expiresOn} />
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => { if (!open) setArchiving(null) }}
        title={`Archive ${archiving?.title ?? 'this announcement'}?`}
        description="It leaves the feed and keeps its read record — who read it and when is the evidence that a notice reached people, which is the whole point of having recorded it."
        confirmLabel="Archive it"
        loading={archiveAnnouncement.isPending}
        error={error}
        onConfirm={() => {
          if (!archiving) return
          setError(null)
          archiveAnnouncement.mutate(archiving.id, {
            onSuccess: () => setArchiving(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That announcement could not be archived.'),
          })
        }}
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        size="lg"
        title={draft?.id ? 'Edit announcement' : 'New announcement'}
        submitLabel="Save"
        loading={saveAnnouncement.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setDialogError(null)
            try {
              await saveAnnouncement.mutateAsync({
                id: draft.id,
                body: {
                  legalEntityId: entities.data?.[0]?.id ?? null,
                  title: draft.title,
                  body: draft.body,
                  audienceKind: 'ALL',
                  expiresOn: draft.expiresOn || null,
                  pinned: draft.pinned,
                },
              })
              setDraft(null)
            } catch (caught) {
              setDialogError(toApiProblem(caught)?.detail ?? 'That could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="announcement-title">Title</Label>
              <Input
                id="announcement-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="announcement-body">What you want to say</Label>
              <Input
                id="announcement-body" value={draft.body}
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="announcement-expires">Comes down on</Label>
                <DatePicker
                  id="announcement-expires" value={draft.expiresOn}
                  onChange={(event) => setDraft({ ...draft, expiresOn: event.target.value })}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id="announcement-pinned" type="checkbox" className="size-4"
                    checked={draft.pinned}
                    onChange={(event) => setDraft({ ...draft, pinned: event.target.checked })}
                  />
                  Keep it at the top
                </label>
              </div>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
